import { buttonVariants } from '@talentbabu/ui';
import {
  ArrowUp,
  BellRing,
  Building2,
  CalendarCheck,
  Facebook,
  Instagram,
  Mail,
  MapPin,
  Phone,
  Youtube,
  Linkedin,
} from 'lucide-react';
import { Link } from 'react-router';
import { useAuthStore } from '../../app/auth-store';
import { Logo } from '../../components/Logo';
import { GradientText } from '../../components/site';
import { homeImage } from '../../features/home/image-credits';
import { useLocations } from '../../features/masters/api';
import { usePublicSettings } from '../../features/settings/public-settings';

const COLUMNS = [
  {
    title: 'Company',
    links: [
      { to: '/about', label: 'About Us' },
      { to: '/services', label: 'Recruitment Services' },
      { to: '/companies', label: 'Verified Companies' },
      { to: '/blog', label: 'Blogs' },
      { to: '/contact', label: 'Contact Us' },
    ],
  },
  {
    title: 'Job Seekers',
    links: [
      { to: '/jobs', label: 'Browse All Jobs' },
      { to: '/register', label: 'Create Free Profile' },
      { to: '/blog', label: 'Career Advice' },
      { to: '/login', label: 'Candidate Login' },
    ],
  },
  {
    title: 'Employers',
    links: [
      { to: '/employer/register', label: 'Post a Job' },
      { to: '/pricing', label: 'Plans & Pricing' },
      { to: '/contact', label: 'Talk to a Recruiter' },
      { to: '/login', label: 'Employer Login' },
    ],
  },
];

const SOCIAL_ICONS = [
  { key: 'facebook', label: 'Facebook', Icon: Facebook },
  { key: 'youtube', label: 'YouTube', Icon: Youtube },
  { key: 'instagram', label: 'Instagram', Icon: Instagram },
  { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin },
] as const;

export function SiteFooter() {
  const user = useAuthStore((s) => s.user);
  const { data: settings } = usePublicSettings();
  const { data: locations } = useLocations();
  const profile = settings?.['company.profile'];
  const state = profile?.state ?? 'Chhattisgarh';
  // const cities = (locations ?? []).filter((l) => l.state === state).slice(0, 5);
  // Footer cities keep this order, but only those that still exist in the admin-managed location list are shown.
  const cities = ['Durg', 'Bhilai', 'Raipur', 'Bilaspur', 'Korba'].flatMap((name) => {
    const match = locations?.find((l) => l.name.toLowerCase() === name.toLowerCase());
    return match ? [{ id: match.id, name: match.name }] : [];
  });
  const legalName = profile?.legalName ?? 'TalentBabu';
  // Admin-managed (Content → Social Media); platforms without a URL get no icon.
  const socialLinks = SOCIAL_ICONS.flatMap(({ key, label, Icon }) => {
    const href = settings?.['social.links']?.[key]?.trim();
    return href ? [{ href, label, Icon }] : [];
  });

  return (
    <footer className="mt-20">
      {!user && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="relative isolate overflow-hidden rounded-3xl border border-slate-200">
            <img
              src={homeImage('steel plant')}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 -z-10 h-full w-full object-cover"
            />
            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#050816] via-[#050816]/90 to-[#050816]/40" />
            <div className="grid gap-8 px-6 py-12 sm:px-12 sm:py-16 lg:grid-cols-[1.4fr_1fr] lg:items-center">
              <div>
                <h2 className="text-3xl leading-[1.1] font-bold text-white sm:text-[44px]">
                  {/* Hiring in {state}? <GradientText>Let’s talk.</GradientText> */}
                  {/* Hiring in India? <GradientText>Let’s talk.</GradientText> */}
                  Looking For <GradientText>Best Talent?</GradientText>
                </h2>
                <p className="mt-4 max-w-xl text-lg text-white/75">
                  {/* Post jobs to verified candidates, or let a TalentBabu recruiter source, screen and shortlist for you. */}
                  Post jobs, connect with talented candidates, and find the right fit for your team.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row lg:justify-end">
                <Link
                  to="/employer/register"
                  className={buttonVariants({ size: 'lg', className: 'site-glow h-12 px-6 text-base' })}
                >
                  <Building2 className="h-5 w-5" /> Hire with TalentBabu
                </Link>
                <Link
                  to="/contact"
                  className={buttonVariants({
                    size: 'lg',
                    variant: 'outline',
                    className: 'h-12 border-white/30 bg-white/5 px-6 text-base text-white hover:bg-white/10',
                  })}
                >
                  <CalendarCheck className="h-5 w-5" /> Book a Consultation
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className={user ? 'bg-slate-950' : 'mt-20 bg-slate-950'}>
        <div className="mx-auto grid max-w-7xl gap-10 px-4 pt-16 pb-10 sm:grid-cols-2 sm:px-6 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1.2fr]">
          <div className="sm:col-span-2 lg:col-span-1">
            <Logo inverted imgClassName="h-16 sm:h-20" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-500">
              {profile?.tagline ?? 'Connecting Talent With Opportunity'}. Verified candidates, verified employers and a
              dedicated recruitment team in {profile?.city ?? 'Raipur'}.
            </p>
            {cities.length > 0 && (
              <p className="mt-5 text-sm text-slate-500">
                <span className="font-semibold text-slate-700">Jobs in </span>
                {cities.map((c, i) => (
                  <span key={c.id}>
                    {i > 0 && ' · '}
                    <Link to={`/jobs?city=${encodeURIComponent(c.name)}`} className="hover:text-brand-700">
                      {c.name}
                    </Link>
                  </span>
                ))}
              </p>
            )}
          </div>

          {COLUMNS.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <h2 className="font-display text-[15px] font-semibold text-slate-900">{col.title}</h2>
              <ul className="mt-5 space-y-3 text-sm">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-slate-500 transition-colors hover:text-slate-900">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          <div>
            <h2 className="font-display text-[15px] font-semibold text-slate-900">Contact</h2>
            <ul className="mt-5 space-y-3 text-sm text-slate-500">
              {profile?.email && (
                <li>
                  <a href={`mailto:${profile.email}`} className="flex items-center gap-2.5 hover:text-slate-900">
                    <Mail className="h-4 w-4 shrink-0 text-brand-600" aria-hidden />
                    {profile.email}
                  </a>
                </li>
              )}
              {profile?.phone && (
                <li>
                  <a href={`tel:${profile.phone}`} className="flex items-center gap-2.5 hover:text-slate-900">
                    <Phone className="h-4 w-4 shrink-0 text-brand-600" aria-hidden />
                    {profile.phone}
                  </a>
                </li>
              )}
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" aria-hidden />
                <span>
                  {[profile?.address, profile?.city ?? 'Raipur', state, profile?.pincode].filter(Boolean).join(', ')}
                </span>
              </li>
            </ul>
            <div className="mt-5 flex items-center gap-3">
              {socialLinks.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`TalentBabu on ${label}`}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-brand-600 transition-colors hover:border-slate-400 hover:text-slate-900"
                >
                  <Icon className="h-5 w-5" aria-hidden />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                <BellRing className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <h2 className="font-display text-base font-semibold text-slate-900">Get Job Alerts</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Save a search and we’ll send matching jobs by email, SMS or WhatsApp. You choose the channel.
                </p>
              </div>
            </div>
            <Link to={user ? '/jobs' : '/register'} className={buttonVariants({ className: 'site-glow shrink-0' })}>
              {user ? 'Search & Save' : 'Create Profile'}
            </Link>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-7xl border-t border-slate-200 px-4 sm:px-6">
          <div className="flex flex-col items-center gap-3 py-6 text-center text-[13px] text-slate-500 sm:flex-row sm:justify-between sm:text-left">
            <p>
              © {new Date().getFullYear()} {legalName}. All rights reserved.
            </p>
            <nav aria-label="Legal" className="flex flex-wrap justify-center gap-x-4 gap-y-1">
              <Link to="/terms" className="hover:text-slate-900">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="hover:text-slate-900">
                Privacy Policy
              </Link>
              <Link to="/refund-policy" className="hover:text-slate-900">
                Refunds Policy
              </Link>
              {/* <Link to="/image-credits" className="hover:text-slate-900">
                Image credits
              </Link> */}
            </nav>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900"
            >
              <ArrowUp className="h-3.5 w-3.5" aria-hidden /> Back to Top
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
